/**
 * DemoModeModal — modal wyboru trybu Production/Demo.
 *
 * Wywoływany z menu avatara. Po zmianie MUSI zrobić hard reload
 * (`window.location.href = "/"`), bo React Query/SWR cache trzyma
 * dane ze starej bazy — inaczej user zobaczy stary widok.
 *
 * Kill-switch: gdy `demo_enabled_globally === false` (ENV
 * `DEMO_MODE_ENABLED=false`), przełączenie na Demo jest zablokowane
 * — modal pokazuje info zamiast toggle.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoModeModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState("production");
  const [globallyEnabled, setGloballyEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/users/me/mode`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setCurrent(r.data?.mode || "production");
        setGloballyEnabled(r.data?.demo_enabled_globally ?? true);
      })
      .catch(() => {
        if (!cancelled) toast.error("Nie udało się pobrać aktualnego trybu.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSwitch = async (target) => {
    if (target === current) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/users/me/mode`,
        { mode: target },
        { withCredentials: true }
      );
      toast.success(
        target === "demo"
          ? "Przełączono w tryb Demo. Trwa przeładowanie…"
          : "Przełączono w tryb Production. Trwa przeładowanie…"
      );
      // Hard reload — czyszczenie cache Query/SWR/React state.
      setTimeout(() => {
        window.location.href = "/";
      }, 800);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Nie udało się przełączyć trybu."
      );
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px]"
        data-testid="demo-mode-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Tryb aplikacji
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm text-gray-600">
            Demo Mode przełącza wszystkie dane biznesowe na osobną bazę
            demonstracyjną. Twoje realne dane Production pozostają całkowicie
            odseparowane — nic ich nie modyfikuje.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Wczytuję…
            </div>
          ) : (
            <>
              {/* Production option */}
              <button
                onClick={() => handleSwitch("production")}
                disabled={saving || current === "production"}
                data-testid="demo-mode-choose-production"
                className={`w-full text-left rounded-lg border-2 p-4 transition-all hover:border-[#0F1F3D] disabled:opacity-70 ${
                  current === "production"
                    ? "border-[#0F1F3D] bg-[#0F1F3D]/5"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#0F1F3D] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">Production</span>
                      {current === "production" && (
                        <span className="text-[10px] uppercase tracking-wide bg-[#0F1F3D] text-white px-1.5 py-0.5 rounded-full font-semibold">
                          Aktywny
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Prawdziwe dane firmy: faktury, wyciągi, KSeF, paczki płatności.
                    </p>
                  </div>
                </div>
              </button>

              {/* Demo option */}
              <button
                onClick={() => handleSwitch("demo")}
                disabled={saving || current === "demo" || !globallyEnabled}
                data-testid="demo-mode-choose-demo"
                className={`w-full text-left rounded-lg border-2 p-4 transition-all hover:border-amber-500 disabled:opacity-70 disabled:cursor-not-allowed ${
                  current === "demo"
                    ? "border-amber-500 bg-amber-50"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">Demo</span>
                      {current === "demo" && (
                        <span className="text-[10px] uppercase tracking-wide bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-semibold">
                          Aktywny
                        </span>
                      )}
                      {!globallyEnabled && (
                        <span className="text-[10px] uppercase tracking-wide bg-gray-300 text-gray-700 px-1.5 py-0.5 rounded-full font-semibold">
                          Wyłączony (ENV)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Osobna baza z fake danymi (start: pusta). KSeF i integracje bankowe
                      są w tym trybie wyłączone. Realne dane firmy pozostają nietknięte.
                    </p>
                  </div>
                </div>
              </button>

              <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 p-3 text-[11px] text-gray-600 leading-snug">
                Po przełączeniu aplikacja zostanie automatycznie
                przeładowana, żeby wyczyścić cache widoków. To zajmuje ok. 1 sekundę.
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="demo-mode-cancel-btn"
          >
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DemoModeModal;
